/**
 * Browser side of "Sign in with Microsoft".
 *
 * Authorisation-code flow with PKCE against the Entra app registration (SPA
 * platform, so no client secret): we send the user to Microsoft, Microsoft sends
 * them back to /auth/microsoft/callback with a code, we redeem the code for an
 * ID token directly with Microsoft, and hand the ID token to our API, which
 * verifies it and returns the usual Mobstr session token.
 *
 * The API supplies the client id and a one-time nonce (POST /auth/microsoft/start),
 * so nothing here is baked into the build.
 */
import axios from "axios";

const STORAGE_KEY = "mobstr_ms_sso";
const API = () => process.env.REACT_APP_API_ROOT;

export const callbackUrl = (origin = window.location.origin, path = "/auth/microsoft/callback") => `${origin}${path}`;

const base64url = (bytes) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

export function randomString(byteLength = 32, cryptoImpl = window.crypto) {
  const bytes = new Uint8Array(byteLength);
  cryptoImpl.getRandomValues(bytes);
  return base64url(bytes);
}

/** PKCE S256 challenge for a verifier. */
export async function pkceChallenge(verifier, cryptoImpl = window.crypto) {
  const digest = await cryptoImpl.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64url(digest);
}

/** Whether Microsoft sign-in is configured on the server (for showing the button). */
export async function fetchMicrosoftConfig() {
  try {
    const { data } = await axios.get(`${API()}/auth/microsoft/config`);
    return data;
  } catch {
    return { enabled: false };
  }
}

/** Build the authorise URL (exported for tests). */
export function authorizeUrl({ authority, clientId, redirectUri, state, nonce, challenge, loginHint }) {
  const url = new URL(`${authority}/oauth2/v2.0/authorize`);
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("response_mode", "query");
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("scope", "openid profile email");
  url.searchParams.set("state", state);
  url.searchParams.set("nonce", nonce);
  url.searchParams.set("code_challenge", challenge);
  url.searchParams.set("code_challenge_method", "S256");
  if (loginHint) url.searchParams.set("login_hint", loginHint);
  else url.searchParams.set("prompt", "select_account");
  return url.toString();
}

/** Send the user to Microsoft. `returnTo` is where to land after signing in. */
export async function startMicrosoftSignIn({ loginHint, returnTo = "/dashboard" } = {}) {
  const { data } = await axios.post(`${API()}/auth/microsoft/start`);
  if (!data?.clientId || !data?.nonce) throw new Error("Microsoft sign-in is not configured");
  const verifier = randomString(48);
  const state = randomString(24);
  const redirectUri = callbackUrl(window.location.origin, data.callbackPath);
  sessionStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ verifier, state, redirectUri, clientId: data.clientId, authority: data.authority, returnTo, startedAt: Date.now() })
  );
  window.location.assign(
    authorizeUrl({
      authority: data.authority,
      clientId: data.clientId,
      redirectUri,
      state,
      nonce: data.nonce,
      challenge: await pkceChallenge(verifier),
      loginHint,
    })
  );
}

export class MicrosoftSignInError extends Error {
  constructor(code, message) {
    super(message || code);
    this.code = code;
  }
}

/**
 * Finish the sign-in on the callback page. Resolves with
 * { token, returnTo } on success, or { adminConsent: true } after an
 * administrator granted consent; throws MicrosoftSignInError otherwise.
 */
export async function completeMicrosoftSignIn(search = window.location.search, { fetchImpl = window.fetch.bind(window) } = {}) {
  const params = new URLSearchParams(search);
  if (params.get("admin_consent")) {
    if (params.get("admin_consent").toLowerCase() === "true") return { adminConsent: true };
    throw new MicrosoftSignInError("consent_declined");
  }
  if (params.get("error")) {
    const description = params.get("error_description") || "";
    if (params.get("error") === "access_denied") throw new MicrosoftSignInError("cancelled", description);
    if (/AADSTS65001|consent/i.test(description)) throw new MicrosoftSignInError("consent_required", description);
    throw new MicrosoftSignInError("microsoft_error", description);
  }

  let saved;
  try {
    saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
  } finally {
    sessionStorage.removeItem(STORAGE_KEY);
  }
  const code = params.get("code");
  if (!saved || !code || params.get("state") !== saved.state || Date.now() - saved.startedAt > 15 * 60 * 1000) {
    throw new MicrosoftSignInError("expired");
  }

  const tokenRes = await fetchImpl(`${saved.authority}/oauth2/v2.0/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      client_id: saved.clientId,
      grant_type: "authorization_code",
      scope: "openid profile email",
      code,
      redirect_uri: saved.redirectUri,
      code_verifier: saved.verifier,
    }).toString(),
  });
  const tokens = await tokenRes.json().catch(() => ({}));
  if (!tokenRes.ok || !tokens.id_token) throw new MicrosoftSignInError("microsoft_error", tokens.error_description);

  const { data } = await axios.post(`${API()}/login/microsoft`, { idToken: tokens.id_token });
  if (data?.success?.token) return { token: data.success.token, returnTo: saved.returnTo || "/dashboard" };
  throw new MicrosoftSignInError(data?.error?.code || "unexpected_error");
}

/** What to tell the user for each failure. */
export const MICROSOFT_ERRORS = {
  no_account: "There's no Mobstr account for that Microsoft account. Ask your Mobstr administrator to invite you, using the email address you sign in to Microsoft with.",
  wrong_tenant: "That Microsoft account belongs to a different organisation from the one your Mobstr account is set up for. Try again with your work account.",
  sso_not_enabled: "Your organisation hasn't set up Microsoft sign-in for Mobstr yet. Sign in with your email and password instead, or ask your administrator.",
  personal_account: "Personal Microsoft accounts can't be used. Sign in with your work or school account.",
  already_linked: "Your Mobstr account is already linked to a different Microsoft account. Contact support@mobstr.io if this is unexpected.",
  account_disabled: "Your Mobstr account is disabled. Contact your administrator.",
  sso_not_configured: "Microsoft sign-in isn't available right now.",
  consent_required: "Your organisation needs to approve Mobstr in Microsoft Entra before you can sign in. Ask your IT administrator to grant consent (Mobstr Settings has the link).",
  consent_declined: "Consent was not granted, so Microsoft sign-in can't be used yet.",
  cancelled: "Microsoft sign-in was cancelled.",
  expired: "The sign-in took too long or was started in another tab. Please try again.",
  invalid_token: "Microsoft sign-in couldn't be verified. Please try again.",
  microsoft_error: "Microsoft couldn't complete the sign-in. Please try again.",
  default: "Something went wrong signing in with Microsoft. Please try again.",
};
export const microsoftErrorMessage = (code) => MICROSOFT_ERRORS[code] || MICROSOFT_ERRORS.default;
